import React, { useState, useEffect } from 'react';
import { isYouTubeShort } from '../utils/youtubeUtils';
import { detectVideoSource } from '../utils/videoSource';
import YouTubeEmbed from './YouTubeEmbed';
import DriveVideoEmbed from './DriveVideoEmbed';
import '../styles/global.css';
import { useConfig } from '../config/ConfigContext';

/**
 * SummaryVideo component for displaying the lecture's summary video.
 *
 * URL source handling
 *   The Summary Video URL column on the lecture spreadsheet can contain
 *   either a YouTube URL (legacy: most lectures' summary videos live on
 *   the center's YouTube channel) OR a Google Drive share link (newer:
 *   summary videos generated locally via the /generate-video skill are
 *   uploaded to Drive instead of YouTube). The renderer routes between
 *   the two via utils/videoSource.detectVideoSource() so the column is
 *   format-agnostic and downstream callers don't need to know.
 *
 * Format detection
 *   Portrait/square/landscape inference is YouTube-specific (Shorts URLs
 *   look different). Drive videos default to landscape since the pipeline
 *   currently produces landscape masters. If we ever start producing
 *   portrait Drive videos a hint can be added later (?orientation=portrait
 *   in the URL, a separate column, etc.) — leaving that out for now to
 *   avoid speculative complexity.
 */
const SummaryVideo = ({ url }) => {
  const { theme } = useConfig();
  const [loading, setLoading] = useState(true);

  const videoUrl = url || '';
  const source = detectVideoSource(videoUrl);

  // Format detection is meaningful for YouTube (Shorts → portrait). For
  // Drive we currently always produce landscape masters. If that changes,
  // extend videoSource.detectVideoSource() to read a format hint.
  const determineFormat = () => {
    if (source.kind === 'youtube' && isYouTubeShort(videoUrl)) return 'portrait';
    if (videoUrl.toLowerCase().includes('square')) return 'square';
    return 'landscape';
  };
  const format = determineFormat();

  useEffect(() => {
    console.log(`[SummaryVideo] url=${videoUrl} kind=${source.kind} format=${format}`);
    setLoading(false);
  }, [videoUrl, source.kind, format]);

  // ──────────────────────────────────────────────────────────────────
  // Empty state
  // ──────────────────────────────────────────────────────────────────
  if (!videoUrl) {
    return (
      <div className="video-placeholder" style={{
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.lightGray,
        borderRadius: theme.borderRadius.medium,
        margin: '20px auto',
        maxWidth: '800px',
        height: '400px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: theme.colors.primary, marginBottom: '20px' }}>Summary Video</h2>
        <p>No summary video is available for this lecture yet. Check back later for updates.</p>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // Unrecognised URL (neither YouTube nor Drive)
  // ──────────────────────────────────────────────────────────────────
  if (source.kind === 'none') {
    return (
      <div className="error-message" style={{
        padding: '20px',
        backgroundColor: '#ffebee',
        color: '#d32f2f',
        borderRadius: theme.borderRadius.medium,
        margin: '20px auto',
        maxWidth: '800px',
        textAlign: 'center'
      }}>
        <h2 style={{ marginBottom: '10px' }}>Video Error</h2>
        <p>The video URL provided isn't a recognised YouTube or Google Drive link.</p>
        <p style={{ marginTop: '10px' }}><strong>URL:</strong> {videoUrl}</p>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // Playable
  // ──────────────────────────────────────────────────────────────────
  return (
    <div className="summary-video-container" style={{
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <h2 style={{
        color: theme.colors.primary,
        marginBottom: '20px',
        textAlign: 'center',
        fontFamily: theme.fonts.secondary
      }}>
        Summary Video
      </h2>

      <div className={`video-wrapper ${format}`} style={{
        width: '100%',
        maxWidth: format === 'portrait' ? '400px' : '800px',
        margin: '0 auto'
      }}>
        {source.kind === 'youtube' && (
          <YouTubeEmbed videoId={source.id} format={format} />
        )}
        {source.kind === 'drive' && (
          <DriveVideoEmbed streamUrl={source.streamUrl} format={format} />
        )}
      </div>
    </div>
  );
};

export default SummaryVideo;
